import { useEffect, useRef, useState } from 'react';
import { api2, ApiError, uploadFile, type MessageDto, type ThreadSummary } from '../api.ts';
import { type AttachedFile } from '../components/AttachField.tsx';
import AutoTextarea from '../components/AutoTextarea.tsx';

/**
 * Threads, WhatsApp-shaped: a list that opens into a conversation. Volunteers
 * write to their coordinators and to any group they have been put in; they
 * cannot start one with another volunteer, which is deliberate.
 */
export default function Messages({ openWith }: { openWith?: string | null }): React.ReactElement {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [coordinators, setCoordinators] = useState<{ id: string; name: string; role: string }[]>([]);
  const [draft, setDraft] = useState(openWith ?? '');
  const [attached, setAttached] = useState<AttachedFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const loadThreads = () =>
    api2
      .threads()
      .then((res) => setThreads(res.threads))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load your messages.'));

  useEffect(() => {
    void loadThreads();
    api2
      .myCoordinators()
      .then((res) => setCoordinators(res.coordinators))
      .catch(() => setCoordinators([]));
  }, []);

  const open = async (id: string) => {
    setOpenId(id);
    setError(null);
    try {
      const res = await api2.thread(id);
      setMessages(res.messages);
      await loadThreads();
      requestAnimationFrame(() => bottom.current?.scrollIntoView({ block: 'end' }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that conversation.');
    }
  };

  // Uploads happen on pick, so Send only has an id to include.
  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { file: saved } = await uploadFile(file);
      setAttached({ id: saved.id, name: saved.name, mime: file.type });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That upload did not go through.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const send = async () => {
    // A photo with no words is a fine message.
    if (!openId || (!draft.trim() && !attached)) return;
    setBusy(true);
    try {
      const res = await api2.send(openId, draft, attached?.id);
      setMessages((prev) => [...prev, res.message]);
      setDraft('');
      setAttached(null);
      await loadThreads();
      requestAnimationFrame(() => bottom.current?.scrollIntoView({ block: 'end' }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send.');
    } finally {
      setBusy(false);
    }
  };

  const startWith = async (userId: string) => {
    try {
      const { threadId } = await api2.openDirect(userId);
      await open(threadId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start that conversation.');
    }
  };

  /* ------------------------------------------------------ a conversation */

  if (openId) {
    const thread = threads.find((t) => t.id === openId);
    return (
      <div className="stack-lg">
        <header className="row">
          <button className="btn ghost" onClick={() => setOpenId(null)} style={{ paddingLeft: 0 }}>
            ← All messages
          </button>
        </header>
        <div>
          <h1>{thread?.title ?? 'Conversation'}</h1>
          {thread?.subtitle && <p className="soft small">{thread.subtitle}</p>}
        </div>

        {error && (
          <p className="notice alert" role="alert">
            {error}
          </p>
        )}

        <div className="stack" style={{ gap: 10 }}>
          {messages.map((message) => (
            <div
              key={message.id}
              className="card"
              style={{
                padding: '12px 15px',
                maxWidth: '85%',
                alignSelf: message.mine ? 'flex-end' : 'flex-start',
                background: message.mine ? 'var(--indigo-wash)' : 'var(--card)',
              }}
            >
              {/* Groups label every bubble; a two-person thread does not need it. */}
              {!message.mine && thread?.kind === 'group' && <div className="kicker">{message.senderName}</div>}
              {message.body && (
                <p style={{ marginTop: thread?.kind === 'group' && !message.mine ? 4 : 0 }}>{message.body}</p>
              )}
              {message.attachment &&
                (message.attachment.mime.startsWith('image/') ? (
                  <a href={`/api/files/${message.attachment.id}`} target="_blank" rel="noopener">
                    <img
                      src={`/api/files/${message.attachment.id}`}
                      alt={message.attachment.name}
                      style={{ maxWidth: '100%', borderRadius: 10, marginTop: message.body ? 8 : 0 }}
                    />
                  </a>
                ) : (
                  <a href={`/api/files/${message.attachment.id}`} style={{ marginTop: message.body ? 8 : 0 }}>
                    {message.attachment.name}
                  </a>
                ))}
              <div className="small muted" style={{ marginTop: 4 }}>
                {formatWhen(message.createdAt)}
              </div>
            </div>
          ))}
          {messages.length === 0 && <p className="muted">Nothing here yet. Say hello.</p>}
          <div ref={bottom} />
        </div>

        <div className="stack" style={{ gap: 8 }}>
          <div className="field">
            <label htmlFor="draft">Write a message</label>
            <AutoTextarea id="draft" value={draft} onChange={(e) => setDraft(e.target.value)} />
          </div>
          {attached && (
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
              <span className="pill">{attached.name}</span>
              <button className="btn ghost" onClick={() => setAttached(null)}>
                Remove
              </button>
            </div>
          )}
          <div className="row">
            <button
              className="btn"
              onClick={() => void send()}
              disabled={busy || uploading || (!draft.trim() && !attached)}
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
            <button
              className="btn quiet"
              onClick={() => fileInput.current?.click()}
              disabled={uploading || Boolean(attached)}
            >
              {uploading ? 'Uploading…' : 'Attach'}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={(e) => void pickFile(e.target.files?.[0])}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------- the list */

  return (
    <div className="stack-lg">
      <header>
        <h1>Messages</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          Your coordinators, and any group you have been added to.
        </p>
      </header>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}

      <section className="stack">
        {threads.map((thread) => (
          <button
            key={thread.id}
            className="card stack"
            style={{ gap: 6, textAlign: 'left', cursor: 'pointer', border: '1px solid var(--line)' }}
            onClick={() => void open(thread.id)}
          >
            <div className="row">
              <b>{thread.title}</b>
              {thread.kind === 'group' && <span className="pill">group</span>}
              {thread.unread > 0 && (
                <span className="pill marigold" style={{ marginLeft: 'auto' }}>
                  {thread.unread} new
                </span>
              )}
            </div>
            {thread.lastMessage && (
              <p className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {thread.lastMessage}
              </p>
            )}
          </button>
        ))}
        {threads.length === 0 && (
          <div className="card empty">
            <p>No conversations yet.</p>
          </div>
        )}
      </section>

      {coordinators.length > 0 && (
        <section className="stack">
          <h2>Your line</h2>
          <p className="small muted">Anything you cannot settle locally goes up a level. Start wherever fits.</p>
          {coordinators.map((person) => (
            <div key={person.id} className="card row" style={{ padding: 16 }}>
              <div>
                <b>{person.name}</b>
                <div className="small muted">{roleLabel(person.role)}</div>
              </div>
              <button className="btn quiet" style={{ marginLeft: 'auto' }} onClick={() => void startWith(person.id)}>
                Write
              </button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function roleLabel(role: string): string {
  switch (role) {
    case 'city_coordinator':
      return 'Your city coordinator';
    case 'regional_coordinator':
      return 'Your regional coordinator';
    default:
      return 'Volunteer support · North America';
  }
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
